import React, { useState, useEffect, useMemo } from 'react';
import { 
    Search, Briefcase, MapPin, Building2, DollarSign, 
    Clock, Tag, Globe, Sparkles, Filter, ChevronRight, 
    ChevronDown, ExternalLink, ArrowRight, Star, 
    TrendingUp, Calendar, Info, Layers, X
} from 'lucide-react';
import { api } from '../../utils/api';
import { toast } from 'sonner';
import NavigationHeader from '../../components/ui/NavigationHeader';
import DOMPurify from 'dompurify';

const Jobs = () => {
    const [loading, setLoading] = useState(true);
    const [searchQuery, setSearchQuery] = useState('');
    const [selectedCategory, setSelectedCategory] = useState(null);
    const [expandedCats, setExpandedCats] = useState({});
    const [activeTags, setActiveTags] = useState([]);
    const [selectedJob, setSelectedJob] = useState(null);

    // Data State
    const [categories, setCategories] = useState([]);
    const [jobs, setJobs] = useState([]);
    const [tags, setTags] = useState([]);

    // Filter State
    const [selectedJobTypes, setSelectedJobTypes] = useState([]);
    const [selectedExperience, setSelectedExperience] = useState([]);
    const [locationSearch, setLocationSearch] = useState('');
    const [salaryRange, setSalaryRange] = useState('');

    useEffect(() => {
        fetchInitialData();
    }, []);

    const fetchInitialData = async () => {
        try {
            setLoading(true);
            const [catsRes, jobsRes, tagsRes] = await Promise.all([
                api.get('/api/jobs/categories'),
                api.get('/api/jobs'),
                api.get('/api/jobs/tags')
            ]);
            setCategories(catsRes.data || []);
            setJobs(jobsRes.data || []);
            setTags(tagsRes.data || []);
            
            // Auto-expand top level
            const initialExpanded = {};
            (catsRes.data || []).forEach(cat => {
                if (!cat.parent_id) initialExpanded[cat.id] = true;
            });
            setExpandedCats(initialExpanded);
        } catch (err) {
            toast.error('Failed to load career data');
        } finally {
            setLoading(false);
        }
    };

    const toggleTag = (tag) => {
        setActiveTags(prev => 
            prev.includes(tag) ? prev.filter(t => t !== tag) : [...prev, tag]
        );
    };

    const toggleCat = (id, e) => {
        e.stopPropagation();
        setExpandedCats(prev => ({ ...prev, [id]: !prev[id] }));
    };

    const renderCategoryTree = (parentId = null, depth = 0) => {
        const items = categories.filter(c => c.parent_id === parentId);
        if (items.length === 0) return null;

        return (
            <div className={`space-y-1 ${depth > 0 ? 'ml-4 mt-1 border-l-2 border-slate-100 pl-3' : ''}`}>
                {items.map(cat => {
                    const hasChildren = categories.some(c => c.parent_id === cat.id);
                    const isExpanded = expandedCats[cat.id];
                    const isSelected = selectedCategory?.id === cat.id;

                    return (
                        <div key={cat.id}>
                            <div 
                                onClick={() => setSelectedCategory(isSelected ? null : cat)}
                                className={`
                                    flex items-center gap-3 px-3 py-2 rounded-xl cursor-pointer transition-all group
                                    ${isSelected ? 'bg-emerald-50 text-emerald-700' : 'hover:bg-slate-50 text-slate-600'}
                                `}
                            >
                                <div className={`p-1.5 rounded-lg ${isSelected ? 'bg-emerald-100' : 'bg-slate-100 group-hover:bg-emerald-50'}`}>
                                    <Briefcase size={12} className={isSelected ? 'text-emerald-600' : 'text-slate-400 group-hover:text-emerald-500'} />
                                </div>
                                <span className="text-[13px] font-bold flex-1">{cat.name}</span>
                                {hasChildren && (
                                    <button 
                                        onClick={(e) => toggleCat(cat.id, e)}
                                        className="p-1 hover:bg-white rounded-md transition-colors"
                                    >
                                        {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                                    </button>
                                )}
                            </div>
                            {isExpanded && renderCategoryTree(cat.id, depth + 1)}
                        </div>
                    );
                })}
            </div>
        );
    };

    // Filter Logic
    const filteredJobs = useMemo(() => {
        return jobs.filter(job => {
            // Search Query
            const matchesSearch = !searchQuery || 
                job.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                job.company_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
                (job.description && job.description.toLowerCase().includes(searchQuery.toLowerCase()));

            // Category Filter
            const matchesCategory = !selectedCategory || 
                (job.category_ids && job.category_ids.includes(selectedCategory.id));

            // Tags Filter
            const matchesTags = activeTags.length === 0 || 
                activeTags.every(tag => job.tags && job.tags.includes(tag));

            // Job Type Filter
            const matchesJobType = selectedJobTypes.length === 0 || 
                selectedJobTypes.includes(job.employment_type?.toLowerCase());

            // Experience Level Filter
            const matchesExperience = selectedExperience.length === 0 || 
                selectedExperience.includes(job.experience_level?.toLowerCase());

            // Location Filter
            const matchesLocation = !locationSearch || 
                job.location?.toLowerCase().includes(locationSearch.toLowerCase());

            // Salary Filter
            const matchesSalary = !salaryRange || 
                (job.salary_range && job.salary_range.toLowerCase().includes(salaryRange.toLowerCase()));

            return matchesSearch && matchesCategory && matchesTags && matchesJobType && matchesExperience && matchesLocation && matchesSalary;
        });
    }, [jobs, searchQuery, selectedCategory, activeTags, selectedJobTypes, selectedExperience, locationSearch, salaryRange]);

    const toggleJobType = (type) => {
        const t = type.toLowerCase();
        setSelectedJobTypes(prev => 
            prev.includes(t) ? prev.filter(item => item !== t) : [...prev, t]
        );
    };

    const toggleExperience = (level) => {
        const l = level.toLowerCase();
        setSelectedExperience(prev => 
            prev.includes(l) ? prev.filter(item => item !== l) : [...prev, l]
        );
    };

    return (
        <div className="min-h-screen bg-white flex flex-col font-sans relative">
            <div className="absolute top-0 left-0 right-0 h-[450px] bg-gradient-to-b from-emerald-50 via-emerald-50/20 to-white pointer-events-none z-0" />
            <div className="absolute -top-24 -left-24 w-96 h-96 bg-emerald-100/40 rounded-full blur-[100px] pointer-events-none" />
            <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-emerald-50/30 rounded-full blur-[120px] pointer-events-none translate-x-1/2 -translate-y-1/2" />

            <NavigationHeader />

            <main className="flex-1 max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 pt-24 pb-24 relative z-10">
                <div className="mb-10 text-center max-w-4xl mx-auto">
                    <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-emerald-100 text-emerald-700 text-[10px] font-bold mb-4 tracking-wide">
                        <Sparkles size={12} className="text-amber-500 fill-amber-500" />
                        <span className="uppercase tracking-widest">Exclusive Career Opportunities</span>
                    </div>
                    <h1 className="text-2xl md:text-3xl font-bold text-slate-900 tracking-tight mb-4">
                        Your Next Big <span className="text-emerald-600">Career Move</span> Starts Here.
                    </h1>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
                    <div className="col-span-12 lg:col-span-3 sticky top-[72px] z-30">
                        <div className="bg-white rounded-2xl border border-slate-200 p-5 flex flex-col shadow-sm max-h-[calc(100vh-100px)]">
                            <div className="flex items-center justify-between mb-6 shrink-0">
                                <h3 className="text-[10px] font-bold text-slate-400tracking-[0.2em] flex items-center gap-2">
                                    <Filter className="text-emerald-600" size={14} />
                                    Filters
                                </h3>
                                {(selectedCategory || activeTags.length > 0 || selectedJobTypes.length > 0 || selectedExperience.length > 0 || locationSearch || salaryRange) && (
                                    <button 
                                        onClick={() => {
                                            setSelectedCategory(null);
                                            setActiveTags([]);
                                            setSearchQuery('');
                                            setSelectedJobTypes([]);
                                            setSelectedExperience([]);
                                            setLocationSearch('');
                                            setSalaryRange('');
                                        }}
                                        className="text-[10px] font-bold text-rose-500 hover:text-rose-600 transition-colors tracking-widest"
                                    >
                                        CLEAR ALL
                                    </button>
                                )}
                            </div>
                            
                            <div className="overflow-y-auto custom-scrollbar pr-2 flex-1 space-y-8">
                                {/* Search Location */}
                                <div>
                                    <div className="flex items-center gap-2 text-slate-800 font-bold mb-4 text-[12px]tracking-wider">
                                        <MapPin size={14} className="text-emerald-500" />
                                        Location
                                    </div>
                                    <div className="relative">
                                        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" size={14} />
                                        <input 
                                            type="text"
                                            placeholder="City, State, or Remote"
                                            value={locationSearch}
                                            onChange={(e) => setLocationSearch(e.target.value)}
                                            className="w-full pl-9 pr-4 py-2 bg-slate-50 border border-slate-100 rounded-lg text-[13px] font-bold placeholder:text-slate-400 focus:outline-none focus:border-emerald-500 transition-all"
                                        />
                                    </div>
                                </div>

                                {/* Categories Section */}
                                <div>
                                    <div className="flex items-center gap-2 text-slate-800 font-bold mb-4 text-[12px]tracking-wider">
                                        <Layers size={14} className="text-emerald-500" />
                                        Categories
                                    </div>
                                    {loading ? (
                                        <div className="space-y-3">
                                            {[1,2,3].map(i => <div key={i} className="h-8 bg-slate-50 animate-pulse rounded-lg" />)}
                                        </div>
                                    ) : (
                                        renderCategoryTree()
                                    )}
                                </div>

                                {/* Employment Type */}
                                <div>
                                    <div className="flex items-center gap-2 text-slate-800 font-bold mb-4 text-[12px]tracking-wider">
                                        <Briefcase size={14} className="text-emerald-500" />
                                        Job Type
                                    </div>
                                    <div className="space-y-2">
                                        {['Full-time', 'Part-time', 'Contract', 'Internship', 'Freelance'].map(type => (
                                            <label key={type} className="flex items-center gap-3 group cursor-pointer">
                                                <div 
                                                    onClick={() => toggleJobType(type)}
                                                    className={`w-4 h-4 rounded border flex items-center justify-center transition-all ${selectedJobTypes.includes(type.toLowerCase()) ? 'border-emerald-500 bg-emerald-500' : 'border-slate-300 bg-white group-hover:border-emerald-500'}`}
                                                >
                                                    {selectedJobTypes.includes(type.toLowerCase()) && <div className="w-2 h-2 rounded-sm bg-white" />}
                                                </div>
                                                <span className={`text-[13px] transition-colors ${selectedJobTypes.includes(type.toLowerCase()) ? 'text-slate-900 font-bold' : 'text-slate-600 group-hover:text-slate-900'}`}>{type}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                {/* Experience Level */}
                                <div>
                                    <div className="flex items-center gap-2 text-slate-800 font-bold mb-4 text-[12px]tracking-wider">
                                        <TrendingUp size={14} className="text-emerald-500" />
                                        Experience
                                    </div>
                                    <div className="space-y-2">
                                        {['Entry', 'Junior', 'Mid', 'Senior', 'Lead'].map(level => (
                                            <label key={level} className="flex items-center gap-3 group cursor-pointer">
                                                <div 
                                                    onClick={() => toggleExperience(level)}
                                                    className={`w-4 h-4 rounded border flex items-center justify-center transition-all ${selectedExperience.includes(level.toLowerCase()) ? 'border-emerald-500 bg-emerald-500' : 'border-slate-300 bg-white group-hover:border-emerald-500'}`}
                                                >
                                                    {selectedExperience.includes(level.toLowerCase()) && <div className="w-2 h-2 rounded-sm bg-white" />}
                                                </div>
                                                <span className={`text-[13px] transition-colors ${selectedExperience.includes(level.toLowerCase()) ? 'text-slate-900 font-bold' : 'text-slate-600 group-hover:text-slate-900'}`}>{level}</span>
                                            </label>
                                        ))}
                                    </div>
                                </div>

                                {/* Salary Range */}
                                <div>
                                    <div className="flex items-center gap-2 text-slate-800 font-bold mb-4 text-[12px]tracking-wider">
                                        <span className="text-emerald-500 text-sm font-black">$</span>
                                        Salary Range
                                    </div>
                                    <select 
                                        value={salaryRange}
                                        onChange={(e) => setSalaryRange(e.target.value)}
                                        className="w-full px-3 py-2 bg-slate-50 border border-slate-100 rounded-lg text-[13px] font-bold text-slate-600 focus:outline-none focus:border-emerald-500 transition-all appearance-none cursor-pointer"
                                    >
                                        <option value="">Any Salary</option>
                                        <option value="50k">$50k+</option>
                                        <option value="100k">$100k+</option>
                                        <option value="150k">$150k+</option>
                                        <option value="200k">$200k+</option>
                                        <option value="300k">$300k+</option>
                                    </select>
                                </div>

                                {/* Hot Tags */}
                                <div>
                                    <div className="flex items-center gap-2 text-slate-800 font-bold mb-4 text-[12px]tracking-wider">
                                        <Tag size={14} className="text-emerald-500" />
                                        Skills
                                    </div>
                                    <div className="flex flex-wrap gap-2">
                                        {tags.slice(0, 15).map(tag => (
                                            <button 
                                                key={tag}
                                                onClick={() => toggleTag(tag)}
                                                className={`
                                                    px-2.5 py-1 rounded-lg text-[11px] font-bold transition-all duration-200 border-2
                                                    ${activeTags.includes(tag) 
                                                        ? 'bg-emerald-600 text-white border-emerald-600' 
                                                        : 'bg-white text-slate-500 border-slate-100 hover:border-emerald-200 hover:text-emerald-600'}
                                                `}
                                            >
                                                {tag}
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Main Feed */}
                    <div className="col-span-12 lg:col-span-9 space-y-6">
                        {/* Search Bar - Sticky */}
                        <div className="sticky top-[72px] z-30 pb-4 bg-white/5 backdrop-blur-md -mx-2 px-2 transition-all">
                            <div className="bg-white rounded-[1.5rem] border border-slate-200 p-2 flex flex-col md:flex-row gap-2 shadow-lg shadow-slate-100/50">
                                <div className="flex-1 relative group">
                                    <Search className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-400" size={18} />
                                    <input 
                                        type="text" 
                                        placeholder="Search roles, companies, or keywords..." 
                                        value={searchQuery}
                                        onChange={(e) => setSearchQuery(e.target.value)}
                                        className="w-full pl-12 pr-6 py-3.5 bg-slate-50 border-none rounded-xl text-[14px] text-slate-700 font-bold placeholder:text-slate-400 focus:outline-none transition-all"
                                    />
                                </div>
                                <button className="px-6 py-3.5 bg-emerald-600 text-white rounded-xl font-bold text-[13px] hover:bg-emerald-700 transition-all flex items-center justify-center gap-2">
                                    <Filter size={16} />
                                    SEARCH
                                </button>
                            </div>
                        </div>

                        {/* Active Filters Display */}
                        {(selectedCategory || activeTags.length > 0) && (
                            <div className="flex flex-wrap items-center gap-3 px-2">
                                <span className="text-[9px] font-bold text-slate-400tracking-[0.2em]">Active:</span>
                                {selectedCategory && (
                                    <span className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-700 text-[11px] font-bold">
                                        <Layers size={10} />
                                        {selectedCategory.name}
                                        <button onClick={() => setSelectedCategory(null)} className="hover:text-rose-500 ml-1.5"><X size={14} /></button>
                                    </span>
                                )}
                                {activeTags.map(tag => (
                                    <span key={tag} className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-emerald-50 border border-emerald-100 text-emerald-700 text-[11px] font-bold">
                                        <Tag size={10} />
                                        {tag}
                                        <button onClick={() => toggleTag(tag)} className="hover:text-rose-500 ml-1.5"><X size={14} /></button>
                                    </span>
                                ))}
                            </div>
                        )}

                        {/* Jobs List */}
                        <div className="space-y-4">
                            {loading ? (
                                Array(3).fill(0).map((_, i) => (
                                    <div key={i} className="bg-white rounded-2xl p-8 border border-slate-200 animate-pulse h-40" />
                                ))
                            ) : filteredJobs.length === 0 ? (
                                <div className="bg-white rounded-2xl p-12 text-center border-2 border-dashed border-slate-200 bg-slate-50/30">
                                    <p className="text-lg font-normal text-slate-500 mb-2">No Matches Found</p>
                                    <p className="text-sm text-slate-500 font-medium mb-8">We couldn't find any jobs matching those criteria.</p>
                                    <button 
                                        onClick={() => {
                                            setSearchQuery('');
                                            setSelectedCategory(null);
                                            setActiveTags([]);
                                        }}
                                        className="px-6 py-2.5 bg-white border border-emerald-600 text-emerald-600 rounded-xl font-bold text-xs hover:bg-emerald-50 transition-all"
                                    >
                                        RESET FILTERS
                                    </button>
                                </div>
                            ) : (
                                filteredJobs.map(job => (
                                    <div 
                                        key={job.id} 
                                        className={`
                                            group bg-white rounded-2xl p-5 border transition-all duration-200
                                            ${job.is_featured ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-100 hover:border-emerald-200 hover:bg-emerald-50/5'}
                                        `}
                                    >
                                        <div className="flex flex-col md:flex-row gap-5">
                                            {/* Company Logo */}
                                            <div className="flex-shrink-0">
                                                <div className="w-12 h-12 rounded-xl bg-white border border-slate-100 p-2 flex items-center justify-center">
                                                    {job.company_logo_url ? (
                                                        <img src={job.company_logo_url} alt={job.company_name} className="w-full h-full object-contain" />
                                                    ) : (
                                                        <Building2 size={20} className="text-slate-300" />
                                                    )}
                                                </div>
                                            </div>

                                            {/* Job Info */}
                                            <div className="flex-1 space-y-3">
                                                <div className="flex flex-wrap justify-between items-start gap-4">
                                                    <div className="space-y-0.5">
                                                        <div className="flex items-center gap-2">
                                                            <h3 className="text-[17px] font-bold text-slate-900 group-hover:text-emerald-600 transition-colors tracking-tight">
                                                                {job.title}
                                                            </h3>
                                                        </div>
                                                        <div className="flex flex-wrap items-center gap-2 text-slate-500 font-bold text-[12px]">
                                                            <span className="text-slate-700">{job.company_name}</span>
                                                            <span className="w-1 h-1 rounded-full bg-slate-300" />
                                                            <span className="flex items-center gap-1"><MapPin size={12} className="text-emerald-500" /> {job.location}</span>
                                                        </div>
                                                    </div>
                                                    <div className="flex flex-col items-end">
                                                        <div className="text-emerald-600 font-bold text-lg tracking-tight">
                                                            {job.salary_range || 'Competitive'}
                                                        </div>
                                                        <div className="flex items-center gap-1 text-slate-400 text-[9px] font-boldtracking-widest mt-0.5">
                                                            Posted {new Date(job.posted_at).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                                        </div>
                                                    </div>
                                                </div>

                                                <div className="flex flex-wrap items-center gap-3">
                                                    <div className="flex items-center gap-1.5 bg-slate-50 px-2.5 py-1 rounded-lg border border-slate-100 text-[10px] font-bold text-slate-600tracking-widest">
                                                        <Clock size={12} className="text-emerald-500" />
                                                        {job.employment_type}
                                                    </div>
                                                    <div className="flex items-center gap-1.5 bg-slate-50 px-2.5 py-1 rounded-lg border border-slate-100 text-[10px] font-bold text-slate-600tracking-widest">
                                                        <Briefcase size={12} className="text-emerald-500" />
                                                        {job.experience_level}
                                                    </div>
                                                    <button 
                                                        onClick={() => setSelectedJob(job)}
                                                        className="flex items-center gap-1 text-indigo-600 font-bold text-[10px]tracking-widest hover:text-indigo-700 transition-colors"
                                                    >
                                                        DETAILS
                                                        <ChevronRight size={12} />
                                                    </button>
                                                    <div className="flex-1" />
                                                    {job.tags && job.tags.slice(0, 3).map(tag => (
                                                        <div key={tag} className="text-emerald-600/60 text-[10px] font-boldtracking-[0.1em]">
                                                            #{tag}
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>

                                            {/* Action Button */}
                                            <div className="flex items-center justify-end md:border-l border-slate-100 md:pl-6">
                                                <a 
                                                    href={job.apply_url} 
                                                    target="_blank" 
                                                    rel="noopener noreferrer"
                                                    className="w-full md:w-auto px-6 py-3 bg-slate-900 text-white rounded-xl font-bold text-[13px] hover:bg-emerald-600 transition-all flex items-center justify-center gap-2 active:scale-95"
                                                >
                                                    APPLY
                                                    <ExternalLink size={16} />
                                                </a>
                                            </div>
                                        </div>
                                    </div>
                                ))
                            )}
                        </div>

                        {/* Disclaimer Card */}
                        <div className="bg-emerald-50/50 rounded-2xl p-6 border border-emerald-100/50 flex gap-4 items-start">
                            <div className="w-10 h-10 rounded-xl bg-white flex items-center justify-center shrink-0 border border-emerald-100">
                                <Info className="text-emerald-500" size={18} />
                            </div>
                            <div className="space-y-1">
                                <h4 className="text-sm font-bold text-slate-900 tracking-tight">Career Insight</h4>
                                <p className="text-[12px] text-slate-600 leading-relaxed font-medium">
                                    prepxai curates these roles for high-impact candidates. We are not directly affiliated with the companies.
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </main>

            {/* Job Details Modal */}
            {selectedJob && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-slate-900/40 backdrop-blur-sm">
                    <div className="bg-white w-full max-w-2xl max-h-[85vh] rounded-[2rem] overflow-hidden flex flex-col border border-slate-200 shadow-2xl">
                        {/* Modal Header */}
                        <div className="px-6 py-4 border-b border-slate-100 flex items-start justify-between bg-slate-50/30">
                            <div className="flex gap-4">
                                <div className="w-10 h-10 rounded-xl bg-white border border-slate-100 p-1.5 flex items-center justify-center shrink-0">
                                    {selectedJob.company_logo_url ? (
                                        <img src={selectedJob.company_logo_url} alt={selectedJob.company_name} className="w-full h-full object-contain" />
                                    ) : (
                                        <Building2 size={16} className="text-slate-300" />
                                    )}
                                </div>
                                <div>
                                    <h2 className="text-lg font-bold text-slate-900 tracking-tight mb-0.5">{selectedJob.title}</h2>
                                    <div className="flex items-center gap-2 text-slate-500 font-bold text-[11px]">
                                        <span className="text-slate-900">{selectedJob.company_name}</span>
                                        <span className="w-1 h-1 rounded-full bg-slate-300" />
                                        <span className="flex items-center gap-1"><MapPin size={11} className="text-emerald-500" /> {selectedJob.location}</span>
                                    </div>
                                </div>
                            </div>
                            <button onClick={() => setSelectedJob(null)} className="p-2 hover:bg-slate-100 rounded-lg text-slate-400"><X size={18} /></button>
                        </div>

                        {/* Modal Content */}
                        <div className="flex-1 overflow-y-auto p-6 custom-scrollbar">
                            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
                                <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
                                    <div className="text-[9px] font-bold text-slate-400tracking-widest mb-0.5">Type</div>
                                    <div className="text-[11px] font-bold text-slate-700">{selectedJob.employment_type}</div>
                                </div>
                                <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
                                    <div className="text-[9px] font-bold text-slate-400tracking-widest mb-0.5">Level</div>
                                    <div className="text-[11px] font-bold text-slate-700">{selectedJob.experience_level}</div>
                                </div>
                                <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
                                    <div className="text-[9px] font-bold text-slate-400tracking-widest mb-0.5">Salary</div>
                                    <div className="text-[11px] font-bold text-emerald-600">{selectedJob.salary_range || 'Competitive'}</div>
                                </div>
                                <div className="bg-slate-50 p-3 rounded-xl border border-slate-100">
                                    <div className="text-[9px] font-bold text-slate-400tracking-widest mb-0.5">Posted</div>
                                    <div className="text-[11px] font-bold text-slate-700">{new Date(selectedJob.posted_at).toLocaleDateString()}</div>
                                </div>
                            </div>

                            <div className="space-y-6">
                                <div>
                                    <h3 className="text-[10px] font-bold text-slate-400tracking-[0.2em] mb-3">Job Description</h3>
                                    <div 
                                        className="job-description-content text-slate-600 font-medium leading-relaxed"
                                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(selectedJob.description || "No description provided.") }}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Modal Footer */}
                        <div className="px-6 py-4 border-t border-slate-100 bg-slate-50/30 flex items-center justify-end gap-3">
                            <button onClick={() => setSelectedJob(null)} className="px-5 py-2.5 text-slate-500 font-bold text-[12px] hover:text-slate-800 transition-colors">CLOSE</button>
                            <a 
                                href={selectedJob.apply_url} 
                                target="_blank" 
                                rel="noopener noreferrer"
                                className="px-6 py-2.5 bg-emerald-600 text-white rounded-xl font-bold text-[12px] hover:bg-emerald-700 transition-all flex items-center gap-2"
                            >
                                APPLY NOW
                                <ExternalLink size={14} />
                            </a>
                        </div>
                    </div>
                </div>
            )}

            <style jsx>{`
                .custom-scrollbar::-webkit-scrollbar {
                    width: 6px;
                }
                .custom-scrollbar::-webkit-scrollbar-track {
                    background: transparent;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb {
                    background: #E2E8F0;
                    border-radius: 20px;
                }
                .custom-scrollbar::-webkit-scrollbar-thumb:hover {
                    background: #CBD5E1;
                }
                .job-description-content {
                    font-size: 13px;
                }
                .job-description-content h1, .job-description-content h2, .job-description-content h3 {
                    font-weight: 800;
                    color: #0f172a;
                    margin-top: 1.25rem;
                    margin-bottom: 0.75rem;
                    font-size: 14px;
                }
                .job-description-content p {
                    margin-bottom: 0.75rem;
                }
                .job-description-content ul {
                    list-style-type: disc;
                    padding-left: 1.25rem;
                    margin-bottom: 0.75rem;
                }
                .job-description-content li {
                    margin-bottom: 0.4rem;
                }
            `}</style>
        </div>
    );
};

export default Jobs;
